import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled from 'styled-components';
import customAnalytics from '../../../util/custom-analytics';
import Modal from '../../Modals/Modal';
import { PRIMARY_COLOR, PRIMARY_COLOR_8, PALE_WHITE_2, PALE_WHITE_4, PALE_WHITE_6 } from '../../../appearance/Colors';

const downloadBlob = async ({ url, filename }) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
};

const buildVideoToolsModalStyles = () => ({
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        color: '#ffffff',
        maxWidth: 560,
        width: '92vw',
        borderRadius: 10,
        border: 'none',
        background: '#222729',
        padding: 0,
        overflow: 'hidden'
    }
});

const MediaToolsPanel = React.forwardRef(function MediaToolsPanel({
    message,
    metaData,
    urlMetaIndex,
    onUpload,
    onSaveAsNewMessage,
    onOverwrite,
    onExtractTranscript,
    onScanVideoText,
    onDownloadUrlInfoDump,
    onDownloadRedditJsonDump,
    onProbeUrlDirectDownloadability,
    status,
    setStatus,
    isUploading,
    isPreparingDownload,
    isExtractingTranscript,
    isScanningVideoText,
    isVideoTextScanInBackground = false,
    isTranscriptProcessingInBackground = false,
    isDownloadingUrlInfoDump,
    isDownloadingRedditJsonDump,
    isProbingDirectDownloadability,
    onDownloadVideo,
    onPullLinkMarkdown,
    mediaVariant,
    setMediaVariant,
    videoQuality,
    setVideoQuality,
    audioQuality,
    setAudioQuality,
    canPullLinkMarkdown,
    isPullingLinkMarkdown,
    showVideoActions,
    showYtDlpControls,
    showTranscriptControls,
    transcriptOnlyMode,
    showDeveloperActions,
    showRedditJsonAction,
    canCancelDownload,
    onCancelDownload,
    showOverwriteModal,
    setShowOverwriteModal,
    variant = 'light'
}, ref) {
    const {t: lnT} = useTranslation();

    const [showToolsModal, setShowToolsModal] = React.useState(false);
    const canShowTranscriptControls = typeof showTranscriptControls === 'boolean'
        ? showTranscriptControls
        : showYtDlpControls;
    const [activeVideoToolsTab, setActiveVideoToolsTab] = React.useState(
        transcriptOnlyMode || (!showYtDlpControls && (canShowTranscriptControls || showRedditJsonAction)) ? 'TRANSCRIPT' : 'VIDEO'
    );
    const selectedMediaLabel = mediaVariant === 'AUDIO' ? lnT('folders:audio') : lnT('messages:video');
    const isAudioVariant = mediaVariant === 'AUDIO';
    const isTranscriptTab = activeVideoToolsTab === 'TRANSCRIPT';
    const isTranscriptActionLoading = isExtractingTranscript || isScanningVideoText || isVideoTextScanInBackground || isDownloadingUrlInfoDump || isDownloadingRedditJsonDump || isProbingDirectDownloadability;
    const showTranscriptDeveloperActions = Boolean((showDeveloperActions && showYtDlpControls) || showRedditJsonAction);

    React.useEffect(() => {
        if (transcriptOnlyMode && activeVideoToolsTab !== 'TRANSCRIPT') {
            setActiveVideoToolsTab('TRANSCRIPT');
        }
    }, [transcriptOnlyMode, activeVideoToolsTab]);

    React.useEffect(() => {
        if (!showYtDlpControls && (canShowTranscriptControls || showRedditJsonAction) && activeVideoToolsTab !== 'TRANSCRIPT') {
            setActiveVideoToolsTab('TRANSCRIPT');
        }
    }, [showYtDlpControls, canShowTranscriptControls, showRedditJsonAction, activeVideoToolsTab]);

    const triggerNativeDownload = React.useCallback((media) => {
        if (!media?.url) return false;
        const payload = {
            url: media.url,
            displayName: media.displayName,
            name: media.name,
            size: media.size,
            mime: media.mime
        };

        try {
            if (typeof window !== 'undefined') {
                if (typeof window.nativeDownload === 'function') {
                    window.nativeDownload(payload);
                    return true;
                }
                if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
                    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'download', payload }));
                    return true;
                }
            }
        } catch (err) {
            console.warn('native download bridge error', err);
        }
        return false;
    }, [lnT]);

    const handleDownloadVideo = async () => {
        if (isPreparingDownload) return;
        setShowToolsModal(false);
        try {
            if (setStatus) {
                setStatus(lnT('messages:downloading_b53e8193', { value1: String(selectedMediaLabel.toLowerCase()) }));
            }
            const media = await onDownloadVideo?.({ message, metaData, urlMetaIndex });
            if (!media?.url) {
                throw new Error('Download not available');
            }
            const handedToNative = triggerNativeDownload(media);
            if (!handedToNative && typeof window !== 'undefined') {
                const fallbackExtension = media?.format || (mediaVariant === 'AUDIO' ? 'mp3' : 'mp4');
                const filename = media.displayName || media.name || `${metaData?.title || 'media'}.${fallbackExtension}`;
                await downloadBlob({ url: media.url, filename });
            }

            customAnalytics.message_action({ action: 'Download URL Media', type: 'Link' }, message);
            if (setStatus) {
                setStatus(handedToNative ? lnT('messages:downloadSentToDevice') : lnT('messages:downloadStarted', { value1: String(selectedMediaLabel) }));
            }
        } catch (err) {
            if (setStatus) {
                const errorMessage = String(err?.message || 'unknown error');
                if (/cancel|abort/i.test(errorMessage)) {
                    setStatus(lnT('messages:downloadCancelled'));
                } else {
                    setStatus(lnT('messages:failedToDownload', { value1: String(selectedMediaLabel.toLowerCase()), value2: String(errorMessage) }));
                }
            }
        }
    };

    const handleExtractTranscript = async (requestedFormat) => {
        if (isTranscriptActionLoading) return;
        setShowToolsModal(false);
        try {
            await onExtractTranscript?.({
                message,
                metaData,
                urlMetaIndex,
                transcriptFormat: requestedFormat || 'PLAIN'
            });
        } catch (err) {
            if (setStatus) {
                setStatus(lnT('messages:couldnTExtractTranscript', { value1: String(err.message) }));
            }
        }
    };

    const handleDownloadUrlInfoDump = async () => {
        if (isTranscriptActionLoading) return;
        setShowToolsModal(false);
        try {
            if (setStatus) {
                setStatus(lnT('messages:preparingYtDlpJsonDump'));
            }
            const media = await onDownloadUrlInfoDump?.({ message, metaData, urlMetaIndex });
            if (!media?.url) {
                throw new Error('JSON dump not available');
            }

            const handedToNative = triggerNativeDownload(media);
            if (!handedToNative && typeof window !== 'undefined') {
                const filename = media.displayName || media.name || 'yt-dlp-info.json';
                await downloadBlob({ url: media.url, filename });
            }

            customAnalytics.message_action({ action: 'Download URL Info Dump', type: 'Link' }, message);
            if (setStatus) {
                setStatus(handedToNative ? lnT('messages:jsonDumpSentToDevice') : lnT('messages:jsonDumpDownloadStarted'));
            }
        } catch (err) {
            if (setStatus) {
                const errorMessage = String(err?.message || 'unknown error');
                setStatus(lnT('messages:couldnTDownloadJsonDump', { value1: String(errorMessage) }));
            }
        }
    };

    const handleDownloadRedditJsonDump = async () => {
        if (isTranscriptActionLoading) return;
        setShowToolsModal(false);
        try {
            if (setStatus) {
                setStatus(lnT('messages:savingRedditApiJson'));
            }
            const media = await onDownloadRedditJsonDump?.({ message, metaData, urlMetaIndex });
            if (!media?.url) {
                throw new Error('Reddit JSON save not available');
            }

            customAnalytics.message_action({ action: 'Save Reddit JSON', type: 'Link' }, message);
            if (setStatus) {
                setStatus(lnT('messages:redditJsonSaved'));
            }
        } catch (err) {
            if (setStatus) {
                const errorMessage = String(err?.message || 'unknown error');
                setStatus(lnT('messages:couldnTSaveRedditJson', { value1: String(errorMessage) }));
            }
        }
    };

    const handleProbeUrlDirectDownloadability = async () => {
        if (isTranscriptActionLoading) return;
        setShowToolsModal(false);
        try {
            if (setStatus) {
                setStatus(lnT('messages:preparingDirectDownloadProbe'));
            }
            const media = await onProbeUrlDirectDownloadability?.({ message, metaData, urlMetaIndex });
            if (!media?.url) {
                throw new Error('Direct download probe not available');
            }

            const handedToNative = triggerNativeDownload(media);
            if (!handedToNative && typeof window !== 'undefined') {
                const filename = media.displayName || media.name || 'yt-dlp-direct-probe.json';
                await downloadBlob({ url: media.url, filename });
            }

            customAnalytics.message_action({ action: 'Probe URL Direct Downloadability', type: 'Link' }, message);
            if (setStatus) {
                setStatus(handedToNative ? lnT('messages:directDownloadProbeSentToDevice') : lnT('messages:directDownloadProbeDownloadStarted'));
            }
        } catch (err) {
            if (setStatus) {
                const errorMessage = String(err?.message || 'unknown error');
                setStatus(lnT('messages:couldnTRunDirectDownloadProbe', { value1: String(errorMessage) }));
            }
        }
    };

    const runVideoActionInBackground = (handler, runningStatus, payload) => {
        if (!handler) return;
        setShowToolsModal(false);
        if (setStatus && runningStatus) {
            setStatus(runningStatus);
        }
        void Promise.resolve(handler(payload)).catch((err) => {
            if (setStatus) {
                const errorMessage = err?.message || lnT('messages:unknownError');
                setStatus(lnT('messages:actionFailed', { value1: String(errorMessage) }));
            }
        });
    };

    const handleSaveToMessage = () => {
        runVideoActionInBackground(
            onUpload,
            `Saving ${selectedMediaLabel.toLowerCase()} in background...`,
            { message, urlMetaIndex }
        );
    };

    const handleSaveAsNewMessage = () => {
        runVideoActionInBackground(
            onSaveAsNewMessage,
            `Creating new message and saving ${selectedMediaLabel.toLowerCase()} in background...`,
            { message, metaData, urlMetaIndex }
        );
    };

    const handleSelectVideoTab = () => {
        setActiveVideoToolsTab('VIDEO');
        setMediaVariant?.('VIDEO');
    };

    const handleSelectAudioTab = () => {
        setActiveVideoToolsTab('AUDIO');
        setMediaVariant?.('AUDIO');
    };

    const handleSelectTranscriptTab = () => {
        setActiveVideoToolsTab('TRANSCRIPT');
    };

    React.useImperativeHandle(ref, () => ({
        openToolsModal: () => {
            if (transcriptOnlyMode || (!showYtDlpControls && (canShowTranscriptControls || showRedditJsonAction))) {
                setActiveVideoToolsTab('TRANSCRIPT');
            } else if (showYtDlpControls && activeVideoToolsTab !== 'TRANSCRIPT') {
                setActiveVideoToolsTab(mediaVariant === 'AUDIO' ? 'AUDIO' : 'VIDEO');
            }
            setShowToolsModal(true);
        },
        saveArticle: () => {
            if (!canPullLinkMarkdown || isUploading || isPullingLinkMarkdown) {
                return;
            }
            onPullLinkMarkdown?.({ message, metaData, urlMetaIndex });
        }
    }), [
        activeVideoToolsTab,
        canPullLinkMarkdown,
        isPullingLinkMarkdown,
        isUploading,
        mediaVariant,
        message,
        metaData,
        onPullLinkMarkdown,
        canShowTranscriptControls,
        showRedditJsonAction,
        showYtDlpControls,
        transcriptOnlyMode,
        urlMetaIndex
    ]);

    return (
        <PanelContainer $hasVisibleContent={Boolean(status || (showVideoActions && canCancelDownload))}>
            {status && <StatusMessage $variant={variant}>{status}</StatusMessage>}
            {showVideoActions && canCancelDownload ? (
                <SecondaryActions>
                    <SecondaryButton
                        $variant={variant}
                        onClick={onCancelDownload}
                    >{lnT('messages:stopCurrentDownload')}</SecondaryButton>
                </SecondaryActions>
            ) : null}

            <Modal
                isOpen={showToolsModal}
                onRequestClose={() => setShowToolsModal(false)}
                style={buildVideoToolsModalStyles()}
                contentLabel={lnT('messages:mediaTools')}
            >
                <VideoToolsModalBody>
                    <VideoModalTitle>{lnT('messages:mediaTools_b4e661a1')}</VideoModalTitle>
                    {!transcriptOnlyMode ? (
                        <VideoModalDescription>{lnT('messages:selectMediaOptionsThenContinue')}</VideoModalDescription>
                    ) : null}
                    {(showYtDlpControls || canShowTranscriptControls) ? (
                        <ControlStack>
                            {!transcriptOnlyMode && showYtDlpControls ? (
                                <ModalTabsRow>
                                    <ModalTabButton
                                        type="button"
                                        $active={activeVideoToolsTab === 'VIDEO'}
                                        onClick={handleSelectVideoTab}
                                        disabled={isUploading || isPreparingDownload || isTranscriptActionLoading}
                                    >{lnT('messages:video')}</ModalTabButton>
                                    <ModalTabButton
                                        type="button"
                                        $active={activeVideoToolsTab === 'AUDIO'}
                                        onClick={handleSelectAudioTab}
                                        disabled={isUploading || isPreparingDownload || isTranscriptActionLoading}
                                    >{lnT('folders:audio')}</ModalTabButton>
                                    <ModalTabButton
                                        type="button"
                                        $active={activeVideoToolsTab === 'TRANSCRIPT'}
                                        onClick={handleSelectTranscriptTab}
                                        disabled={isUploading || isPreparingDownload || isTranscriptActionLoading}
                                    >{lnT('messages:transcript')}</ModalTabButton>
                                </ModalTabsRow>
                            ) : null}

                            {isTranscriptTab ? (
                                <ControlGroup>
                                    <VideoModalLabel>{lnT('messages:transcriptActions')}</VideoModalLabel>
                                </ControlGroup>
                            ) : (
                                <ControlGroup>
                                    <VideoModalLabel>{isAudioVariant ? lnT('messages:audioQuality') : lnT('messages:videoQuality')}</VideoModalLabel>
                                    <SegmentedControl $columns={4}>
                                        {isAudioVariant ? (
                                            <>
                                                <VideoSegmentButton
                                                    type="button"
                                                    $active={audioQuality === 'AUTO'}
                                                    onClick={() => setAudioQuality?.('AUTO')}
                                                    disabled={isUploading || isPreparingDownload}
                                                >{lnT('messages:auto_02862497')}</VideoSegmentButton>
                                                <VideoSegmentButton
                                                    type="button"
                                                    $active={audioQuality === 'HIGH'}
                                                    onClick={() => setAudioQuality?.('HIGH')}
                                                    disabled={isUploading || isPreparingDownload}
                                                >{lnT('messages:high')}</VideoSegmentButton>
                                                <VideoSegmentButton
                                                    type="button"
                                                    $active={audioQuality === 'MEDIUM'}
                                                    onClick={() => setAudioQuality?.('MEDIUM')}
                                                    disabled={isUploading || isPreparingDownload}
                                                >{lnT('messages:medium')}</VideoSegmentButton>
                                                <VideoSegmentButton
                                                    type="button"
                                                    $active={audioQuality === 'LOW'}
                                                    onClick={() => setAudioQuality?.('LOW')}
                                                    disabled={isUploading || isPreparingDownload}
                                                >{lnT('messages:low')}</VideoSegmentButton>
                                            </>
                                        ) : (
                                            <>
                                                <VideoSegmentButton
                                                    type="button"
                                                    $active={videoQuality === 'AUTO'}
                                                    onClick={() => setVideoQuality?.('AUTO')}
                                                    disabled={isUploading || isPreparingDownload}
                                                >{lnT('messages:auto_02862497')}</VideoSegmentButton>
                                                <VideoSegmentButton
                                                    type="button"
                                                    $active={videoQuality === 'P1080'}
                                                    onClick={() => setVideoQuality?.('P1080')}
                                                    disabled={isUploading || isPreparingDownload}
                                                >{lnT('messages:1080p')}</VideoSegmentButton>
                                                <VideoSegmentButton
                                                    type="button"
                                                    $active={videoQuality === 'P720'}
                                                    onClick={() => setVideoQuality?.('P720')}
                                                    disabled={isUploading || isPreparingDownload}
                                                >{lnT('messages:720p')}</VideoSegmentButton>
                                                <VideoSegmentButton
                                                    type="button"
                                                    $active={videoQuality === 'P480'}
                                                    onClick={() => setVideoQuality?.('P480')}
                                                    disabled={isUploading || isPreparingDownload}
                                                >{lnT('messages:480p')}</VideoSegmentButton>
                                            </>
                                        )}
                                    </SegmentedControl>
                                </ControlGroup>
                            )}
                        </ControlStack>
                    ) : null}

                    <VideoModalActionStack>
                        {(canShowTranscriptControls || showRedditJsonAction) && isTranscriptTab ? (
                            <>
                                {canShowTranscriptControls ? (
                                    <>
                                        <VideoSecondaryButton
                                            disabled={isTranscriptActionLoading}
                                            onClick={() => handleExtractTranscript('PLAIN')}
                                        >
                                            {isTranscriptProcessingInBackground ? lnT('messages:gettingTranscript') : (isExtractingTranscript ? lnT('messages:extracting') : lnT('messages:extractPlainTranscript'))}
                                        </VideoSecondaryButton>
                                        <VideoSecondaryButton
                                            disabled={isTranscriptActionLoading}
                                            onClick={() => handleExtractTranscript('TIMESTAMPED')}
                                        >
                                            {isTranscriptProcessingInBackground ? lnT('messages:gettingTranscript') : (isExtractingTranscript ? lnT('messages:extracting') : lnT('messages:extractTimestampedTranscript'))}
                                        </VideoSecondaryButton>
                                        {onScanVideoText ? (
                                            <VideoSecondaryButton
                                                disabled={isTranscriptActionLoading}
                                                onClick={onScanVideoText}
                                            >
                                                {isScanningVideoText || isVideoTextScanInBackground
                                                    ? lnT('messages:reanalyzing')
                                                    : lnT('messages:reanalyzeVideo')}
                                            </VideoSecondaryButton>
                                        ) : null}
                                    </>
                                ) : null}
                                {showTranscriptDeveloperActions ? (
                                    <>
                                        {showRedditJsonAction ? (
                                                <VideoSecondaryButton
                                                    disabled={isTranscriptActionLoading}
                                                    onClick={handleDownloadRedditJsonDump}
                                                >
                                                {isDownloadingRedditJsonDump ? lnT('messages:saving') : lnT('messages:saveRedditApiJson')}
                                            </VideoSecondaryButton>
                                        ) : null}
                                        {showYtDlpControls ? (
                                            <>
                                                <VideoSecondaryButton
                                                    disabled={isTranscriptActionLoading}
                                                    onClick={handleDownloadUrlInfoDump}
                                                >
                                                    {isDownloadingUrlInfoDump ? lnT('messages:preparing') : lnT('messages:downloadYtDlpJsonDump')}
                                                </VideoSecondaryButton>
                                                <VideoSecondaryButton
                                                    disabled={isTranscriptActionLoading}
                                                    onClick={handleProbeUrlDirectDownloadability}
                                                >
                                                    {isProbingDirectDownloadability ? lnT('messages:preparing') : lnT('messages:checkDirectDownloadability')}
                                                </VideoSecondaryButton>
                                            </>
                                        ) : null}
                                    </>
                                ) : null}
                            </>
                        ) : (
                            <>
                                <VideoPrimaryButton disabled={isUploading} onClick={handleSaveToMessage}>
                                    {isUploading ? lnT('messages:working_5474eef8') : lnT('messages:saveToMessage', { value1: String(selectedMediaLabel) })}
                                </VideoPrimaryButton>
                                <VideoSecondaryButton disabled={isUploading} onClick={handleSaveAsNewMessage}>{lnT('messages:saveAsNewMessage')}</VideoSecondaryButton>
                                <VideoSecondaryButton
                                    disabled={isPreparingDownload}
                                    onClick={handleDownloadVideo}
                                >
                                    {isPreparingDownload ? lnT('messages:downloading_a778d349') : lnT('messages:download_8fe1a08e', { value1: String(selectedMediaLabel) })}
                                </VideoSecondaryButton>
                            </>
                        )}
                    </VideoModalActionStack>
                    <VideoModalActions>
                        <VideoSecondaryButton onClick={() => setShowToolsModal(false)}>{lnT('common:close')}</VideoSecondaryButton>
                    </VideoModalActions>
                </VideoToolsModalBody>
            </Modal>

            {showVideoActions && showOverwriteModal && (
                <ModalOverlay>
                    <ModalContent $variant={variant}>
                        <ModalTitle $variant={variant}>{lnT('messages:fileAlreadyExists_ae71a9aa')}</ModalTitle>
                        <ModalBody $variant={variant}>{lnT('messages:doYouWantToOverwriteTheExisting')}</ModalBody>
                        <ModalActions>
                            <SecondaryButton $variant={variant} onClick={() => setShowOverwriteModal(false)}>{lnT('common:cancel')}</SecondaryButton>
                            <PrimaryButton $variant={variant} onClick={() => onOverwrite?.({ message, urlMetaIndex })}>{lnT('messages:overwrite')}</PrimaryButton>
                        </ModalActions>
                    </ModalContent>
                </ModalOverlay>
            )}
        </PanelContainer>
    );
});

MediaToolsPanel.displayName = 'MediaToolsPanel';

const PanelContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: ${props => props.$hasVisibleContent ? '8px' : '0'};
`;

const ControlStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ControlGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SegmentedControl = styled.div`
    display: grid;
    grid-template-columns: repeat(${({ $columns = 2 }) => $columns}, minmax(0, 1fr));
    gap: 6px;
`;

const StatusMessage = styled.div`
    font-size: 12px;
    color: ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(15, 23, 42, 0.6)'};
`;

const SecondaryActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

const baseButtonStyles = `
    width: 100%;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;
`;

const PrimaryButton = styled.button`
    ${baseButtonStyles}
    background-color: ${({ $variant }) => $variant === 'dark' ? PALE_WHITE_4 : PRIMARY_COLOR};
    color: #fff;
    border: 1px solid ${({ $variant }) => $variant === 'dark' ? PALE_WHITE_6 : PRIMARY_COLOR};
    &:disabled {
        background-color: ${({ $variant }) => $variant === 'dark' ? PALE_WHITE_2 : 'rgba(30, 64, 175, 0.6)'};
        border-color: ${({ $variant }) => $variant === 'dark' ? PALE_WHITE_2 : 'rgba(30, 64, 175, 0.6)'};
        color: ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#fff'};
        cursor: default;
    }
    &:not(:disabled):hover {
        background-color: ${({ $variant }) => $variant === 'dark' ? PALE_WHITE_6 : PRIMARY_COLOR_8};
        border-color: ${({ $variant }) => $variant === 'dark' ? PALE_WHITE_6 : PRIMARY_COLOR_8};
        color: #fff;
    }
`;

const SecondaryButton = styled.button`
    ${baseButtonStyles}
    background-color: ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.05)'};
    color: ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(15, 23, 42, 0.85)'};
    border: 1px solid ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(15, 23, 42, 0.12)'};
    &:disabled {
        color: ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(15, 23, 42, 0.4)'};
        border-color: ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 23, 42, 0.08)'};
        cursor: default;
    }
    &:not(:disabled):hover {
        background-color: ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(15, 23, 42, 0.1)'};
        border-color: ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.28)' : 'rgba(15, 23, 42, 0.2)'};
    }
`;

const VideoSegmentButton = styled.button`
    border: ${({ $active }) => $active ? 'none' : '1px solid rgba(255, 255, 255, 0.22)'};
    border-radius: 8px;
    background: ${({ $active }) => $active ? 'rgba(255, 255, 255, 0.9)' : 'transparent'};
    color: ${({ $active }) => $active ? '#1f2426' : 'rgba(255, 255, 255, 0.92)'};
    padding: 8px 12px;
    font-size: 12px;
    font-weight: ${({ $active }) => $active ? 600 : 500};
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    opacity: ${({ disabled }) => disabled ? 0.45 : 1};
    width: 100%;
`;

const ModalTabsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
`;

const ModalTabButton = styled.button`
    border: ${({ $active }) => $active ? 'none' : '1px solid rgba(255, 255, 255, 0.22)'};
    border-radius: 8px;
    background: ${({ $active }) => $active ? 'rgba(255, 255, 255, 0.9)' : 'transparent'};
    color: ${({ $active }) => $active ? '#1f2426' : 'rgba(255, 255, 255, 0.92)'};
    padding: 9px 12px;
    font-size: 12px;
    font-weight: ${({ $active }) => $active ? 600 : 500};
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    opacity: ${({ disabled }) => disabled ? 0.45 : 1};
    width: 100%;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
`;

const ModalContent = styled.div`
    background-color: ${({ $variant }) => $variant === 'dark' ? 'rgba(12, 22, 40, 0.94)' : '#fff'};
    border: 1px solid ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(15, 23, 42, 0.08)'};
    padding: 20px;
    border-radius: 12px;
    width: 320px;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
    color: ${({ $variant }) => $variant === 'dark' ? '#fff' : 'rgba(15, 23, 42, 0.88)'};
`;

const VideoToolsModalBody = styled.div`
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const VideoModalTitle = styled.div`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #ffffff;
`;

const VideoModalDescription = styled.p`
    margin: 0;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
`;

const VideoModalLabel = styled.label`
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.2px;
    color: rgba(255, 255, 255, 0.72);
`;

const VideoModalActionStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

const VideoModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
`;

const VideoSecondaryButton = styled.button`
    border: 1px solid rgba(255, 255, 255, 0.22);
    border-radius: 8px;
    background: transparent;
    color: rgba(255, 255, 255, 0.92);
    padding: 8px 11px;
    font-size: 12px;
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    opacity: ${({ disabled }) => disabled ? 0.45 : 1};
    width: 100%;
`;

const VideoPrimaryButton = styled.button`
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.9);
    color: #1f2426;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};
    opacity: ${({ disabled }) => disabled ? 0.45 : 1};
    width: 100%;
`;

const ModalTitle = styled.div`
    font-weight: 600;
    margin-bottom: 8px;
    color: ${({ $variant }) => $variant === 'dark' ? '#fff' : 'rgba(15, 23, 42, 1)'};
`;

const ModalBody = styled.p`
    margin: 0 0 16px;
    color: ${({ $variant }) => $variant === 'dark' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(15, 23, 42, 0.7)'};
    font-size: 14px;
    line-height: 1.4;
`;

const ModalActions = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
`;

export default MediaToolsPanel;
